import { createRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Route as AdminLayoutRoute } from "./_layout";
import { Badge } from "@/components/base/badges/badges";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { DataTable, type Column } from "@/components/application/data-table/data-table";
import { Button } from "@/components/base/buttons/button";
import { useAdminDocuments } from "../../hooks/useAdminDocuments";
import { useAdminZones } from "../../hooks/useAdminZones";
import type { AdminDocumentRow } from "../../schemas/adminDocuments";

/**
 * GET /api/admin/documents — admin inventář dokumentů.
 *
 * Filtry přes URL search params (`?zoneId=…&docType=…&month=YYYY-MM&q=…`):
 *   - share-friendly URL,
 *   - persistuje přes reload,
 *   - žádné runtime state divergence mezi UI a backendem.
 */

interface DocumentsSearch {
  zoneId?: string;
  docType?: string;
  month?: string;
  q?: string;
}

const ALLOWED_DOC_TYPES = ["invoice", "invoice_statement", "transaction_statement", "card_overview"];
const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

export const Route = createRoute({
  getParentRoute: () => AdminLayoutRoute,
  path: "/admin/documents",
  validateSearch: (s: Record<string, unknown>): DocumentsSearch => ({
    zoneId: typeof s.zoneId === "string" && s.zoneId.length > 0 ? s.zoneId : undefined,
    docType:
      typeof s.docType === "string" && ALLOWED_DOC_TYPES.includes(s.docType)
        ? s.docType
        : undefined,
    month:
      typeof s.month === "string" && MONTH_RE.test(s.month) ? s.month : undefined,
    q: typeof s.q === "string" && s.q.length > 0 ? s.q : undefined,
  }),
  component: AdminDocumentsPage,
});

const DOC_TYPE_LABEL: Record<string, string> = {
  invoice: "Faktura",
  invoice_statement: "Vyúčtování",
  transaction_statement: "Výpis transakcí",
  card_overview: "Přehled karet",
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} kB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatMonthLabel(from: string | null, to: string | null): string {
  if (!from) return "—";
  const d = new Date(from);
  if (Number.isNaN(d.getTime())) return from;
  const monthYear = d.toLocaleString("cs-CZ", { month: "long", year: "numeric" });
  // Pokud period_to není první-poslední den téhož měsíce, fall back na full range —
  // něco je nestandardní a chceme to vidět.
  if (to) {
    const dt = new Date(to);
    if (
      Number.isNaN(dt.getTime()) ||
      dt.getFullYear() !== d.getFullYear() ||
      dt.getMonth() !== d.getMonth()
    ) {
      return `${from} – ${to}`;
    }
  }
  return monthYear;
}

function formatCzDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("cs-CZ");
  } catch {
    return iso;
  }
}

function formatAmount(raw: string | null, currency: string | null): string {
  if (!raw) return "—";
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  const cur = currency ?? "CZK";
  try {
    return new Intl.NumberFormat("cs-CZ", {
      style: "currency",
      currency: cur,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${n.toLocaleString("cs-CZ", { minimumFractionDigits: 2 })} ${cur}`;
  }
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString("cs-CZ", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function monthToRange(month: string | undefined): { periodFrom?: string; periodTo?: string } {
  if (!month) return {};
  const [yearStr, monthStr] = month.split("-");
  const year = Number(yearStr);
  const m = Number(monthStr);
  if (!year || !m) return {};
  const last = new Date(year, m, 0).getDate(); // 0. den následujícího měsíce = poslední den aktuálního
  return {
    periodFrom: `${year}-${monthStr}-01`,
    periodTo: `${year}-${monthStr}-${String(last).padStart(2, "0")}`,
  };
}

function AdminDocumentsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();

  // Debounced search input — URL update až po 300ms po zastavení psaní,
  // aby každé písmenko netriggerovalo nový API request + nová route entry.
  const [searchInput, setSearchInput] = useState(search.q ?? "");
  useEffect(() => {
    setSearchInput(search.q ?? "");
  }, [search.q]);
  useEffect(() => {
    const handle = setTimeout(() => {
      const trimmed = searchInput.trim();
      const next = trimmed.length > 0 ? trimmed : undefined;
      if (next === (search.q ?? undefined)) return;
      void navigate({
        to: "/admin/documents",
        search: { ...search, q: next },
        replace: true,
      });
    }, 300);
    return () => clearTimeout(handle);
  }, [searchInput, search, navigate]);

  const zonesQuery = useAdminZones();

  const range = useMemo(() => monthToRange(search.month), [search.month]);
  const query = useAdminDocuments({
    zoneId: search.zoneId,
    docType: search.docType,
    periodFrom: range.periodFrom,
    periodTo: range.periodTo,
    q: search.q,
  });

  const rows = query.data?.documents ?? [];
  const monthOptions = useMemo(() => buildMonthOptions(rows), [rows]);
  const zoneOptions = useMemo(() => {
    const opts = zonesQuery.data ?? [];
    // Zóna mohla být nastavená (klikem v tabulce / z URL) dřív, než dorazí
    // seznam zón — dodej fallback option, ať select u aktivního filtru
    // neukazuje "Všechny zóny".
    if (search.zoneId && !opts.some((z) => z.zoneId === search.zoneId)) {
      const code = rows.find((r) => r.zone_id === search.zoneId)?.zone_code ?? "?";
      return [...opts, { zoneId: search.zoneId, zoneCode: code }];
    }
    return opts;
  }, [zonesQuery.data, search.zoneId, rows]);

  function patchSearch(patch: Partial<DocumentsSearch>): void {
    void navigate({ to: "/admin/documents", search: { ...search, ...patch } });
  }

  const columns: Column<AdminDocumentRow>[] = [
    {
      key: "zone",
      header: "Zóna",
      sortValue: (r) => r.zone_code,
      cell: (r) => (
        <button
          type="button"
          onClick={() => patchSearch({ zoneId: r.zone_id })}
          className="font-semibold text-brand-secondary hover:underline"
          aria-label={`Filtrovat zónu ${r.zone_code}`}
        >
          {r.zone_code.toUpperCase()}
        </button>
      ),
    },
    {
      key: "doc_id",
      header: "Doc ID",
      sortValue: (r) => r.axigon_doc_id,
      cell: (r) => <span className="font-mono text-xs">{r.axigon_doc_id}</span>,
    },
    {
      key: "type",
      header: "Typ",
      sortValue: (r) => DOC_TYPE_LABEL[r.doc_type] ?? r.doc_type,
      cell: (r) => (
        <Badge color="gray">{DOC_TYPE_LABEL[r.doc_type] ?? r.doc_type}</Badge>
      ),
    },
    {
      key: "period",
      header: "Období",
      sortValue: (r) => (r.period_from ? new Date(r.period_from) : null),
      cell: (r) => formatMonthLabel(r.period_from, r.period_to),
    },
    {
      key: "duzp",
      header: "DUZP",
      sortValue: (r) => (r.duzp ? new Date(r.duzp) : null),
      cell: (r) => (
        <span className={r.duzp ? "" : "text-tertiary"}>{formatCzDate(r.duzp)}</span>
      ),
    },
    {
      key: "amount",
      header: "Cena celkem",
      sortValue: (r) => (r.total_amount ? Number(r.total_amount) : null),
      className: "text-right",
      cell: (r) => (
        <span className={r.total_amount ? "tabular-nums" : "text-tertiary"}>
          {formatAmount(r.total_amount, r.currency)}
        </span>
      ),
    },
    {
      key: "vat",
      header: "Z toho DPH",
      sortValue: (r) => (r.vat_amount ? Number(r.vat_amount) : null),
      className: "text-right",
      cell: (r) => (
        <span className={r.vat_amount ? "tabular-nums" : "text-tertiary"}>
          {formatAmount(r.vat_amount, r.currency)}
        </span>
      ),
    },
    {
      key: "size",
      header: "Velikost",
      sortValue: (r) => r.size_bytes,
      cell: (r) => formatBytes(r.size_bytes),
    },
    {
      key: "status",
      header: "Status",
      sortValue: (r) => r.status,
      cell: (r) => (
        <Badge color={r.status === "synced" ? "success" : "warning"}>{r.status}</Badge>
      ),
    },
    {
      key: "fetched",
      header: "Staženo",
      sortValue: (r) => new Date(r.fetched_at),
      cell: (r) => <time dateTime={r.fetched_at}>{formatDateTime(r.fetched_at)}</time>,
    },
    {
      key: "download",
      header: "PDF",
      cell: (r) => (
        <a
          href={`/api/admin/documents/${r.id}/download`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-brand-secondary hover:underline text-sm"
          aria-label={`Stáhnout ${r.axigon_doc_id}`}
          onClick={(e) => e.stopPropagation()}
        >
          ↓ PDF
        </a>
      ),
    },
  ];

  const hasActiveFilter =
    !!search.q || !!search.zoneId || !!search.docType || !!search.month;

  return (
    <section aria-labelledby="documents-title" className="max-w-7xl mx-auto space-y-6">
      <header className="flex flex-col gap-1">
        <h2 id="documents-title" className="text-2xl font-bold text-primary m-0">
          Dokumenty
        </h2>
        <p className="text-base text-tertiary m-0">
          Faktury (FA) a vyúčtování (VY) stažené ze tří klientských zón Axigonu.
        </p>
      </header>

      <div
        className="flex flex-wrap items-end gap-3"
        role="group"
        aria-label="Filtry dokumentů"
      >
        <label className="flex flex-col gap-1 grow max-w-md">
          <span className="text-sm text-tertiary">Hledat (číslo dokladu)</span>
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="např. 3251109519"
            className="px-3 py-1.5 text-sm rounded-lg ring-1 ring-secondary bg-primary text-primary focus:outline-none focus:ring-2 focus:ring-brand"
            data-testid="docs-search"
            aria-label="Hledat podle čísla dokladu"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-sm text-tertiary">Zóna</span>
          <select
            value={search.zoneId ?? ""}
            onChange={(e) => patchSearch({ zoneId: e.target.value || undefined })}
            className="px-3 py-1.5 text-sm rounded-lg ring-1 ring-secondary bg-primary text-primary focus:outline-none focus:ring-2 focus:ring-brand"
            data-testid="docs-zone-filter"
            aria-label="Filtrovat podle zóny"
          >
            <option value="">Všechny zóny</option>
            {zoneOptions.map((z) => (
              <option key={z.zoneId} value={z.zoneId}>
                {z.zoneCode.toUpperCase()}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-sm text-tertiary">Typ</span>
          <select
            value={search.docType ?? ""}
            onChange={(e) => patchSearch({ docType: e.target.value || undefined })}
            className="px-3 py-1.5 text-sm rounded-lg ring-1 ring-secondary bg-primary text-primary focus:outline-none focus:ring-2 focus:ring-brand"
            data-testid="docs-type-filter"
            aria-label="Filtrovat podle typu dokumentu"
          >
            <option value="">Všechny typy</option>
            {ALLOWED_DOC_TYPES.map((t) => (
              <option key={t} value={t}>
                {DOC_TYPE_LABEL[t] ?? t}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-sm text-tertiary">Měsíc</span>
          <select
            value={search.month ?? ""}
            onChange={(e) => patchSearch({ month: e.target.value || undefined })}
            className="px-3 py-1.5 text-sm rounded-lg ring-1 ring-secondary bg-primary text-primary focus:outline-none focus:ring-2 focus:ring-brand"
            data-testid="docs-month-filter"
            aria-label="Filtrovat podle měsíce"
          >
            <option value="">Všechny měsíce</option>
            {monthOptions.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>

        <div className="ml-auto">
          <Button
            color="secondary"
            size="sm"
            onPress={() => {
              setSearchInput("");
              void navigate({ to: "/admin/documents", search: {} });
            }}
            isDisabled={!hasActiveFilter}
            data-testid="docs-clear-all"
            aria-label="Resetovat všechny filtry"
          >
            Reset filtrů
          </Button>
        </div>
      </div>

      {query.isError && (
        <InlineAlert variant="error">
          Načtení dokumentů selhalo
          {query.error instanceof Error ? `: ${query.error.message}` : "."}
        </InlineAlert>
      )}

      {query.isLoading ? (
        <div className="flex items-center justify-center py-10" aria-live="polite">
          <LoadingIndicator /> <span className="ml-2 text-tertiary">Načítám…</span>
        </div>
      ) : (
        <DataTable
          caption="Dokumenty stažené ze sync běhů"
          captionVisible={false}
          columns={columns}
          rows={rows}
          rowKey={(r) => `${r.zone_id}-${r.axigon_doc_id}-${r.doc_type}`}
          emptyMessage={
            hasActiveFilter
              ? "Žádné dokumenty neodpovídají filtru."
              : "Žádné dokumenty. Spusť synchronizaci v sekci „Synchronizace“."
          }
          loading={query.isFetching}
        />
      )}

      {query.data && rows.length === query.data.limit && (
        <p className="text-sm text-tertiary m-0" role="status">
          Zobrazeno {query.data.limit} dokumentů — pravděpodobně existují další (pagination zatím není
          implementována).
        </p>
      )}
    </section>
  );
}

/**
 * Vybuduje seznam (yearMonth, label) z `period_from` všech řádků. Pouze měsíce,
 * pro které existuje aspoň jeden dokument — žádný dropdown s prázdnými volbami.
 */
function buildMonthOptions(rows: AdminDocumentRow[]): Array<[string, string]> {
  const seen = new Map<string, string>();
  for (const r of rows) {
    if (!r.period_from) continue;
    const d = new Date(r.period_from);
    if (Number.isNaN(d.getTime())) continue;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    if (seen.has(key)) continue;
    seen.set(key, d.toLocaleString("cs-CZ", { month: "long", year: "numeric" }));
  }
  return Array.from(seen.entries()).sort((a, b) => b[0].localeCompare(a[0]));
}
